import { supabase } from '../../supabase';
import { RESUME_REFRESH_SKEW_MS as REFRESH_SKEW_MS } from '../../config/sessionPolicy';

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const SESSION_RETRY_DELAYS = [200, 1000, 3000];

export const isSessionLockError = (error) =>
  error?.name === 'AbortError' &&
  typeof error?.message === 'string' &&
  error.message.includes("Lock broken by another request with the 'steal' option");

export const getSessionWithRetry = async () => {
  for (let attempt = 0; attempt <= SESSION_RETRY_DELAYS.length; attempt++) {
    try {
      return await supabase.auth.getSession();
    } catch (error) {
      if (!isSessionLockError(error) || attempt === SESSION_RETRY_DELAYS.length) {
        throw error;
      }
      await delay(SESSION_RETRY_DELAYS[attempt]);
    }
  }
};

export const isNetworkError = (error) =>
  error?.name === 'AbortError' ||
  (error instanceof TypeError && error.message === 'Failed to fetch') ||
  !navigator.onLine;

/**
 * Ensure the Supabase access token is fresh before issuing queries on app RESUME.
 * iOS suspends a PWA's JS — including the autoRefreshToken timer — while
 * backgrounded; on resume the first query can race an expired token and 401
 * before the SDK refreshes. Call this in visibilitychange / focus handlers BEFORE
 * a refetch so the query carries a valid token. Refreshes only when the token is
 * within REFRESH_SKEW_MS of expiry, so brief tab-switches stay a cheap no-op (no
 * network). Best-effort: never throws — a hard failure is left to the refetch's
 * own error handling (and AuthContext's session checks for a dead refresh token).
 *
 * NOTE: the GoTrue-validated session-liveness probe (revoked-but-not-yet-
 * -expired session detection) used to piggyback here, but that gated it on
 * every call site's own preconditions -- notably CompanySettingsContext's
 * resume effect, which requires a `tenantId` and therefore never ran it for
 * platform super-admins (no tenant). It now lives as its own role-agnostic
 * effect in AuthContext.js, gated on `sessionUser` only. See
 * src/utils/auth/sessionLiveness.js.
 */
export const ensureFreshSession = async () => {
  try {
    const { data: { session } = {} } = await getSessionWithRetry();
    if (!session?.expires_at) return;
    if (session.expires_at * 1000 - Date.now() < REFRESH_SKEW_MS) {
      await supabase.auth.refreshSession();
    }
  } catch (_) {
    // Best-effort — a resume handler must never throw.
  }
};
